/**
 * CompositionLabPanel — exercise the Witness Composition Theorem (T3) live.
 *
 * Builds two PreservationWitnesses χ₁ : E₀ → E₁ and χ₂ : E₁ → E₂, runs
 * each through the validator, then checks the chain join. Lets the user
 * deliberately break the join (kind change · fingerprint mismatch · drift
 * severity drift) and watch the chain refuse with the exact rulebook code.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo, useState } from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import { severityColor } from "../severityColor.js";
import {
  composeWitnesses,
  type ComposeVerdict,
} from "../algorithms/composeWitnesses.js";
import type {
  IssuerCapabilityInput,
  PreservationWitnessInput,
} from "../algorithms/validatePreservation.js";

const ACCENT = "#38bdf8";

const ICAP: IssuerCapabilityInput = {
  issuer_id: "ISSUER-ALPHA",
  may_sign_artifact_types: [
    "EstablishReceipt",
    "PreservationWitness",
    "DriftReceipt",
    "MigrationArtifact",
  ],
  invariant_ids: ["INV-A-NOAMBIENT", "INV-R-BUDGET"],
  kinds: ["Algebraic", "ResourceBound"],
  change_classes: ["code", "config", "deps", "accounting"],
};

const FP_E0 = "fp:E0:noambient";
const FP_E1 = "fp:E1:noambient";
const FP_E1_PRIME = "fp:E1':noambient";
const FP_E2 = "fp:E2:noambient";

function baseWitness(pre: string, post: string): PreservationWitnessInput {
  return {
    invariant_id: "INV-A-NOAMBIENT",
    kind: "Algebraic",
    abs_diff_class: "equivalent",
    drift_severity: "low",
    drift_receipt_present: true,
    issuer_id: "ISSUER-ALPHA",
    fingerprint_continuous: true,
    evidence_present: true,
    pre_fingerprint: pre,
    post_fingerprint: post,
  };
}

interface Preset {
  readonly id: string;
  readonly label: string;
  readonly note: string;
  readonly mutate: (
    chi1: PreservationWitnessInput,
    chi2: PreservationWitnessInput,
  ) => readonly [PreservationWitnessInput, PreservationWitnessInput];
}

const PRESETS: readonly Preset[] = [
  {
    id: "happy",
    label: "Happy chain — χ₂ ∘ χ₁ composes",
    note: "Both witnesses individually type; the chain join fingerprint matches.",
    mutate: (a, b) => [a, b],
  },
  {
    id: "fingerprint-break",
    label: "Fingerprint break at the join (ILG-P-002)",
    note: "χ₁.post_fingerprint ≠ χ₂.pre_fingerprint — the chain is uninhabited.",
    mutate: (a, b) => [a, { ...b, pre_fingerprint: FP_E1_PRIME }],
  },
  {
    id: "kind-mismatch",
    label: "Kind change across the chain (ILG-DR-002)",
    note: "χ₂ flips to ResourceBound — that requires MIG, not chain composition.",
    mutate: (a, b) => [a, { ...b, kind: "ResourceBound" }],
  },
  {
    id: "drift-second",
    label: "Drift severity high in χ₂ (ILG-P-005)",
    note: "χ₂ refuses on its own; the chain inherits the refusal.",
    mutate: (a, b) => [a, { ...b, drift_severity: "high" }],
  },
  {
    id: "abs-unknown-first",
    label: "ABS_DIFF = unknown in χ₁ (ILG-P-004)",
    note: "χ₁ refuses on its own; the chain refuses at stage χ₁.",
    mutate: (a, b) => [
      { ...a, abs_diff_class: "unknown", evidence_present: false },
      b,
    ],
  },
] as const;

function ChainArrow({
  color,
  label,
  sub,
}: {
  readonly color: string;
  readonly label: string;
  readonly sub: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col items-center flex-1 gap-1">
      <span className="font-mono text-[13px] font-bold" style={{ color }}>
        {label}
      </span>
      <svg
        width="100%"
        height="20"
        viewBox="0 0 200 20"
        preserveAspectRatio="none"
      >
        <defs>
          <marker
            id={`arrow-${label}`}
            viewBox="0 -5 10 10"
            refX="10"
            refY="0"
            markerWidth="8"
            markerHeight="8"
            orient="auto"
          >
            <path d="M0,-5L10,0L0,5" fill={color} />
          </marker>
        </defs>
        <line
          x1={4}
          y1={10}
          x2={196}
          y2={10}
          stroke={color}
          strokeWidth={2}
          markerEnd={`url(#arrow-${label})`}
        />
      </svg>
      <span className="text-[var(--ilg-muted)] font-mono text-[10px] tracking-[0.04em]">
        {sub}
      </span>
    </div>
  );
}

function ObjectNode({
  label,
  fingerprint,
  color,
}: {
  readonly label: string;
  readonly fingerprint: string;
  readonly color: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-1.5 min-w-[110px]">
      <div
        className="w-[56px] h-[56px] rounded-[14px] bg-[rgba(6,8,16,0.7)] text-[var(--ilg-bright)] font-mono font-extrabold text-[18px] flex items-center justify-center"
        style={{
          border: `1.5px solid ${color}`,
          boxShadow: `0 0 16px ${color}33`,
        }}
      >
        {label}
      </div>
      <span className="text-[10px] font-mono text-[var(--ilg-muted)] max-w-[130px] text-center break-all">
        {fingerprint}
      </span>
    </div>
  );
}

function VerdictDisplay({
  verdict,
}: {
  readonly verdict: ComposeVerdict;
}): React.JSX.Element {
  if (verdict.outcome === "accept") {
    return (
      <PanelSurface.Card
        glowColor="rgba(34,197,94,0.20)"
        style={{ borderColor: `${panelTokens.accentGreen}66` }}
      >
        <div className="flex items-baseline gap-2.5 flex-wrap">
          <span
            className="font-mono font-extrabold text-[20px]"
            style={{
              color: panelTokens.accentGreen,
              textShadow: `0 0 14px ${panelTokens.accentGreen}55`,
            }}
          >
            χ₂ ∘ χ₁ ∈ GovArrow
          </span>
          {PanelSurface.badge(
            "T3 — Witness Composition",
            panelTokens.accentGreen,
          )}
        </div>
        <p className="m-0 mt-3 text-[13.5px] leading-[1.7] text-[var(--ilg-text)]">
          Both witnesses type individually, the chain join fingerprint matches,
          and the kind / invariant_id are stable. The chain composes — temporal
          integrity holds across the two-step history.
        </p>
        <div className="mt-3">
          <PanelSurface.Mono>{`subject:        ${verdict.chainSubject}
head fingerprint: ${verdict.chainFingerprintHead}
tail fingerprint: ${verdict.chainFingerprintTail}`}</PanelSurface.Mono>
        </div>
      </PanelSurface.Card>
    );
  }
  const color = severityColor(verdict.severity);
  return (
    <PanelSurface.Card
      glowColor={`${color}28`}
      style={{ borderColor: `${color}66` }}
    >
      <div className="flex items-baseline gap-2.5 flex-wrap">
        <span
          className="font-mono font-extrabold text-[18px]"
          style={{
            color,
            textShadow: `0 0 14px ${color}55`,
          }}
        >
          chain refused
        </span>
        {PanelSurface.badge(verdict.rule, color)}
        <PanelSurface.Badge color={panelTokens.muted}>
          at: {verdict.stage}
        </PanelSurface.Badge>
      </div>
      <p className="m-0 mt-3 text-[14px] text-[var(--ilg-bright)] font-semibold leading-[1.6]">
        {verdict.title}
      </p>
      <p className="m-0 mt-2 text-[13px] text-[var(--ilg-text)] leading-[1.7]">
        {verdict.explanation}
      </p>
    </PanelSurface.Card>
  );
}

export function CompositionLabPanel(): React.JSX.Element {
  const [activePreset, setActivePreset] = useState<string>("happy");

  const [chi1, chi2] = useMemo<
    readonly [PreservationWitnessInput, PreservationWitnessInput]
  >(() => {
    const preset = PRESETS.find((p) => p.id === activePreset) ?? PRESETS[0];
    return preset.mutate(baseWitness(FP_E0, FP_E1), baseWitness(FP_E1, FP_E2));
  }, [activePreset]);

  const verdict = useMemo(
    () => composeWitnesses(chi1, chi2, ICAP),
    [chi1, chi2],
  );

  const noteForActive = PRESETS.find((p) => p.id === activePreset)?.note ?? "";

  return (
    <PanelSurface.PanelShell
      title="Composition Lab"
      subtitle="χ₂ ∘ χ₁ — exercise the Witness Composition Theorem (T3) live"
      accentColor={ACCENT}
    >
      <PanelSurface.Card glowColor="rgba(56,189,248,0.10)">
        <PanelSurface.SectionHeading>
          The chain to compose
        </PanelSurface.SectionHeading>
        <div className="mt-[18px] px-2 py-3 flex items-center gap-1">
          <ObjectNode
            label="E₀"
            fingerprint={chi1.pre_fingerprint ?? ""}
            color={panelTokens.accentTeal}
          />
          <ChainArrow
            color={verdictStageColor(verdict, "chi-1")}
            label="χ₁"
            sub={`${chi1.kind === "Algebraic" ? "A" : "R"} · ABS ${chi1.abs_diff_class}`}
          />
          <ObjectNode
            label="E₁"
            fingerprint={chi1.post_fingerprint ?? ""}
            color={joinNodeColor(verdict)}
          />
          <ChainArrow
            color={verdictStageColor(verdict, "chi-2")}
            label="χ₂"
            sub={`${chi2.kind === "Algebraic" ? "A" : "R"} · drift ${chi2.drift_severity}`}
          />
          <ObjectNode
            label="E₂"
            fingerprint={chi2.post_fingerprint ?? ""}
            color={panelTokens.accentTeal}
          />
        </div>
        <p className="m-0 mt-4 text-[13px] text-[var(--ilg-muted)] italic leading-[1.7]">
          {noteForActive}
        </p>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>
          Scenario presets
        </PanelSurface.SectionHeading>
        <div
          className="mt-3 grid gap-2"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          }}
        >
          {PRESETS.map((p) => {
            const active = p.id === activePreset;
            return (
              <PanelSurface.SelectableCard
                key={p.id}
                id={p.id}
                active={active}
                onSelect={setActivePreset}
                color={ACCENT}
              >
                <span
                  className="block text-[13px] font-semibold"
                  style={{
                    color: active ? panelTokens.bright : panelTokens.text,
                  }}
                >
                  {p.label}
                </span>
                <span className="block mt-1 text-[11px] text-[var(--ilg-muted)] font-mono leading-[1.5]">
                  {p.note}
                </span>
              </PanelSurface.SelectableCard>
            );
          })}
        </div>
      </PanelSurface.Card>

      <VerdictDisplay verdict={verdict} />

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>What T3 says</PanelSurface.SectionHeading>
        <p className="mt-3 text-[14px] leading-[1.75] text-[var(--ilg-text)]">
          The Witness Composition Theorem reads: preservation witnesses compose
          only along{" "}
          <PanelSurface.InlineCode>
            fingerprint-continuous
          </PanelSurface.InlineCode>{" "}
          chains with matching{" "}
          <PanelSurface.InlineCode>invariant_id</PanelSurface.InlineCode> and{" "}
          <PanelSurface.InlineCode>kind</PanelSurface.InlineCode>, and with
          drift severity{" "}
          <PanelSurface.InlineCode>
            ∉ {"{high, unknown}"}
          </PanelSurface.InlineCode>
          . The chain composer here is a thin layer over the same{" "}
          <PanelSurface.InlineCode>
            validatePreservationWitness
          </PanelSurface.InlineCode>{" "}
          the witness validator uses — anything that refuses a single witness
          refuses the chain at that stage; the chain-join check then catches the
          genuinely compositional failures (fingerprint break, kind change
          without MIG).
        </p>
        <p className="mt-2.5 text-[13px] text-[var(--ilg-muted)] leading-[1.7]">
          This is why ILG insists governance is{" "}
          <strong>compositional, not cumulative</strong>: a sequence of locally
          green checks is a global green check only when their chain join is
          well-typed.
        </p>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}

function verdictStageColor(
  verdict: ComposeVerdict,
  stage: "chi-1" | "chi-2",
): string {
  if (verdict.outcome === "accept") return panelTokens.accentGreen;
  if (verdict.stage === stage) return panelTokens.accentRed;
  if (verdict.stage === "composition") return panelTokens.accentGreen;
  return panelTokens.muted;
}

function joinNodeColor(verdict: ComposeVerdict): string {
  if (verdict.outcome === "accept") return panelTokens.accentGreen;
  if (verdict.stage === "composition") return panelTokens.accentRed;
  return panelTokens.muted;
}
